import clsx from "clsx";
import { Icon, Sheet } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { mobileNavIcon, mobileNavRoute, mobileNavSub } from "./mobileTabs.js";
import styles from "./MobileShell.module.scss";

type MobileMoreRow = components["schemas"]["MobileMoreRow"];

export interface MoreSheetProps {
  /** The per-role More rows from the server nav document (`nav.mobile.more`). */
  rows: MobileMoreRow[];
  /** Kept MOUNTED and toggled by this (the shell renders it always), so opening is instant — the rows are
   *  already in memory, so warming the sheet costs nothing and removes the mount hitch on tap. */
  open: boolean;
  onNavigate?: (route: string) => void;
  onClose: () => void;
}

/**
 * The More bottom sheet — the per-role rows + coming-soon teasers the server did not promote to a
 * bottom tab, each with its CD subtitle line. A row with a non-null `comingSoon` (Rehearsal /
 * Finances) renders disabled with its pill. Built on the shared `@cntrl/ui` `Sheet` primitive, which
 * owns the scrim/handle/entrance + dialog a11y (topmost Escape, scroll-lock, focus trap); this file
 * owns only the row list. NO Sign out here — the account (avatar) sheet owns it (CD parity).
 */
export function MoreSheet({ rows, open, onNavigate, onClose }: MoreSheetProps) {
  // "For now kill the Rehearsal & Finances sections" (Ryan, ART-206). Those are exactly the coming-soon
  // teasers, so the not-yet-built rows are dropped rather than shown disabled. The disabled-row branch below
  // is kept so returning them is a one-line change once they ship.
  const shown = rows.filter((row) => (row.comingSoon ?? undefined) === undefined);
  return (
    <Sheet title="More" open={open} onClose={onClose} closeLabel="Close More menu">
      <ul className={styles.sheetList}>
        {shown.map((row) => {
          const key = row.key ?? "";
          const Glyph = mobileNavIcon(key);
          const sub = mobileNavSub(key);
          const comingSoon = row.comingSoon ?? undefined;
          const route = comingSoon ? undefined : mobileNavRoute(key);
          return (
            <li key={key}>
              <button
                type="button"
                className={clsx(styles.sheetRow, comingSoon && styles.sheetRowDisabled)}
                disabled={comingSoon !== undefined}
                onClick={() => {
                  if (route) {
                    onNavigate?.(route);
                    onClose();
                  }
                }}
              >
                <Icon icon={Glyph} size={20} />
                <span className={styles.sheetRowBody}>
                  <span className={styles.sheetRowLabel}>{row.label}</span>
                  {sub && <span className={styles.sheetRowSub}>{sub}</span>}
                </span>
                {comingSoon && <span className={styles.sheetBadge}>{comingSoon}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </Sheet>
  );
}
